/**
 * CountdownDirectiveController is a class that controls the countdown functionality in a directive.
 * It handles updates to the countdown, manages the display mode, and interacts with various services.
 */
class CountdownDirectiveController {
    /**
     * Constructs the CountdownDirectiveController.
     * @param {Object} $scope - The AngularJS scope.
     * @param {Object} $element - The DOM element where the directive is applied.
     * @param {Function} $timeout - AngularJS timeout service.
     * @param {Object} $bridges - Service for bridge functionalities.
     * @param {Object} $signals - Service for signal handling.
     * @param {Object} TaskCountdownService - Service for countdown tasks.
     * @param {Object} $translate - AngularJS translation service.
     */
    constructor($scope, $element, $timeout, $bridges, $signals, TaskCountdownService, $translate) {
        this.$scope = $scope;
        this.$element = $element;
        this.$timeout = $timeout;
        this.$bridges = $bridges;
        this.$signals = $signals;
        this.TaskCountdownService = TaskCountdownService;
        this.$translate = $translate;

        this.$scope._logIdentifier = 'CountdownDirectiveController';

        this.$logger = this.$bridges.logger();

        this.$scope.countdown = '';

        this.$scope.taskCountdown = this.TaskCountdownService.getInstance();

        /**
         * Handles updates to the countdown.
         * If the countdown string has not changed, it updates the scope.
         */
        this.onCountdownUpdate = () => {
            if (this.$scope.countdown === this.getCountdownString()) return;
            this.$timeout(() => {
                this.$scope.countdown = this.getCountdownString();
            });
        };
        this.$signals.on('countdownUpdate', this.onCountdownUpdate.bind(this));

        /**
         * Cleanup when the scope is destroyed.
         * Removes the countdown update listener.
         */
        this.$scope.$on('$destroy', () => {
            this.$signals.remove('countdownUpdate', this.onCountdownUpdate);
        });

        this.$scope.modes = this.$scope.taskCountdown.getModeTranslations();
        this.$scope.currentMode = this.$scope.taskCountdown.getDisplayMode();

        /**
         * Returns the current countdown time in milliseconds.
         * @returns {number} The countdown time in milliseconds.
         */
        this.getMillis = () => {
            return this.$scope.taskCountdown.getMillis();
        };

        /**
         * Returns the format string for the countdown.
         * @returns {string} The format string.
         */
        this.getFormat = () => {
            return this.$scope.taskCountdown.getFormat();
        };

        /**
         * Generates the countdown string based on the current state of the countdown.
         * @returns {string} The formatted countdown string.
         */
        this.getCountdownString = () => {
            const model = this.$scope.taskCountdown;

            if (model.isExpired()) {
                if (model.getMode() === "readingTime") {
                    return "";
                }
                return "expired";
            }

            const hours = model.getHours();
            const minutes = model.getMinutes();
            const seconds = model.getSeconds();
            const format = model.getFormat();
            let result = format;

            result = result.replace('{%hh}', hours.toString().padStart(2, '0'));
            result = result.replace('{%mm}', minutes.toString().padStart(2, '0'));
            result = result.replace('{%ss}', seconds.toString().padStart(2, '0'));

            if (model.isPaused()) {
                result = result + this.$translate.instant('SYSTEM.CLOCK.PAUSED');
            }

            return result;
        };

        /**
         * Handles changes to the radio button selection.
         * Updates the display mode of the countdown.
         * @param {string} newMode - The new display mode.
         */
        this.$scope.onRadioButtonChange = (newMode) => {
            this.$logger.info(this.$scope, 'called onRadioButtonChange with', newMode);
            this.$scope.taskCountdown.setDisplayMode(newMode);
        };
    }
}

export default CountdownDirectiveController;

/**
 * Registers the countdown directive with the given module.
 * @param {Object} module - The AngularJS module.
 */
export function registerCountdownDirective(module) {
    module.directive('ibCountdown', function() {
        return {
            restrict: 'E',
            replace: true,
            templateUrl: '/views/ib/components/CountdownDirective.html',
            scope: {
                mode: '@'
            },
            controller: CountdownDirectiveController
        };
    });
}